import React, { useState, useEffect } from "react";
import useStockData from "../hooks/useStockData";
import { useTheme } from "../context/ThemeContext";

const ProductModal = ({
  newProduct,
  setNewProduct,
  handleAddProduct,
  setIsProductModalOpen,
  existingProducts,
}) => {
  const { allProducts } = useStockData();
  const { darkMode } = useTheme();
  const [useComputedMP, setUseComputedMP] = useState(false);

  const handleProductSelect = (e) => {
    const selectedProductId = parseInt(e.target.value, 10);
    const selectedProduct = allProducts?.find(
      (product) => product.id === selectedProductId
    );

    if (selectedProduct) {
      setNewProduct({
        productId: selectedProduct.id,
        materialName: selectedProduct.name,
        price: selectedProduct.price,
        quantityMP: "",
        quantityML: "",
        bizot: "",
        length: "",
        width: "",
        quantity: "",
      });
    }
  };

  useEffect(() => {
    const length = parseFloat(newProduct.length) || 0;
    const width = parseFloat(newProduct.width) || 0;
    const quantity = parseInt(newProduct.quantity, 10) || 0;

    if (useComputedMP && length > 0 && width > 0 && quantity > 0) {
      const computedMP = (length * width * quantity) / 10000;
      setNewProduct((prev) => ({ ...prev, quantityMP: computedMP.toFixed(4) }));
    }
  }, [useComputedMP, newProduct.length, newProduct.width, newProduct.quantity]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80"
      onClick={() => setIsProductModalOpen(false)}
    >
      <div
        className={`p-8 rounded-3xl shadow-xl w-[700px] max-h-[100vh] overflow-y-auto relative transition ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Bezárás "X" gombbal */}
        <button
          onClick={() => setIsProductModalOpen(false)}
          className={`absolute top-3 right-4 text-2xl ${
            darkMode
              ? "text-red-400 hover:text-red-600"
              : "text-red-500 hover:text-red-700"
          }`}
        >
          ✖
        </button>

        <h2
          className={`text-3xl font-semibold mb-6 text-center ${
            darkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          Adăugare Produs
        </h2>

        {/* Termék kiválasztása */}
        <label
          className={`block font-semibold mb-2 ${
            darkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          Selectează Produsul
        </label>
        <select
          value={newProduct?.productId || ""}
          onChange={handleProductSelect}
          className={`w-full p-3 border rounded-2xl transition ${
            darkMode
              ? "border-gray-600 bg-gray-800 text-white"
              : "border-gray-400 bg-gray-100 text-gray-900"
          } mb-4`}
        >
          <option value="">-- Selectează un produs --</option>
          {allProducts.map((product) => (
            <option key={product.id} value={product.id}>
              {product.name} ({product.price} RON)
            </option>
          ))}
        </select>

        <label className="flex items-center mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={useComputedMP}
            onChange={() => {
              setUseComputedMP(!useComputedMP);
              setNewProduct((prev) => ({
                ...prev,
                quantityMP: "",
                length: "",
                width: "",
                quantity: "",
              }));
            }}
            className="mr-2"
          />
          <span>
            {useComputedMP ? "Calcul automat MP" : "Introducere manuală MP"}
          </span>
        </label>
        {useComputedMP && (
          <>
            <label className="block font-semibold mb-2">Lungime</label>
            <input
              type="number"
              placeholder="Lungime"
              value={newProduct?.length ?? ""}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  length: parseFloat(e.target.value) || 0,
                })
              }
              className={`w-full p-3 border rounded-2xl transition ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-gray-900"
              } mb-4`}
            />

            <label className="block font-semibold mb-2">Latime</label>
            <input
              type="number"
              placeholder="Latime"
              value={newProduct?.width ?? ""}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  width: parseFloat(e.target.value) || 0,
                })
              }
              className={`w-full p-3 border rounded-2xl transition ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-gray-900"
              } mb-4`}
            />

            <label className="block font-semibold mb-2">Bucăți</label>
            <input
              type="number"
              placeholder="Bucăți"
              value={newProduct?.quantity ?? ""}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  quantity: parseInt(e.target.value, 10) || 0,
                })
              }
              className={`w-full p-3 border rounded-2xl transition ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-gray-900"
              } mb-4`}
            />
          </>
        )}

        {!useComputedMP && (
          <>
            <label className="block font-semibold mb-2">Cantitate MP</label>
            <input
              type="number"
              placeholder="Cantitate MP"
              value={newProduct?.quantityMP ?? ""}
              onChange={(e) =>
                setNewProduct({ ...newProduct, quantityMP: e.target.value })
              }
              className={`w-full p-3 border rounded-2xl transition ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-gray-900"
              } mb-4`}
            />
          </>
        )}

        {useComputedMP && (
          <>
            <label className="block font-semibold mb-2">
              Cantitate MP (calcul automat)
            </label>
            <input
              type="text"
              placeholder="Cantitate MP"
              value={
                newProduct?.quantityMP
                  ? newProduct.quantityMP.toString().replace(".", ",") // 🔥 Pontok cseréje vesszőre
                  : ""
              }
              readOnly
              className={`w-full p-3 border rounded-2xl transition ${
                darkMode
                  ? "border-gray-600 bg-gray-800 text-white"
                  : "border-gray-400 bg-gray-100 text-gray-900"
              } mb-4`}
            />
          </>
        )}

        <label
          className={`block font-semibold mb-2 ${
            darkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          Cantitate ML
        </label>
        <input
          type="number"
          placeholder="Cantitate ML"
          value={newProduct?.quantityML ?? ""}
          onChange={(e) =>
            setNewProduct({ ...newProduct, quantityML: e.target.value })
          }
          className={`w-full p-3 border rounded-2xl transition ${
            darkMode
              ? "border-gray-600 bg-gray-800 text-white"
              : "border-gray-400 bg-gray-100 text-gray-900"
          } mb-4`}
        />

        <label
          className={`block font-semibold mb-2 ${
            darkMode ? "text-gray-300" : "text-gray-700"
          }`}
        >
          Bizot
        </label>
        <input
          type="text"
          placeholder="Bizot"
          value={newProduct?.bizot ?? ""}
          onChange={(e) =>
            setNewProduct({ ...newProduct, bizot: e.target.value })
          }
          className={`w-full p-3 border rounded-2xl transition ${
            darkMode
              ? "border-gray-600 bg-gray-800 text-white"
              : "border-gray-400 bg-gray-100 text-gray-900"
          } mb-6`}
        />

        {/* Hozzáadás gomb */}
        <button
          className={`w-full py-3 rounded-2xl transition ${
            darkMode
              ? "bg-blue-600 text-white hover:bg-blue-500"
              : "bg-blue-500 text-white hover:bg-blue-400"
          }`}
          onClick={handleAddProduct}
        >
          Adaugă Produs
        </button>
      </div>
    </div>
  );
};

export default ProductModal;
